import { useWorkspaceStore } from '../stores/workspaceStore';
import type { WorkspaceMemberRole } from '@mellify/shared';
import { roleHasPermission } from '@mellify/shared';
import type { Permission } from '@mellify/shared';

/**
 * State shape returned by the useWorkspaceRole hook.
 */
export interface WorkspaceRoleState {
  /** The current user's role within the active workspace. */
  currentMemberRole: WorkspaceMemberRole | null;
  /** Alias for currentMemberRole (used by route guards). */
  role: WorkspaceMemberRole | null;
  /** Update the current member role. */
  setCurrentMemberRole: (role: WorkspaceMemberRole | null) => void;
  /** Whether a role has been determined (workspace is selected). */
  hasRole: boolean;
  /** Whether the role is still loading. */
  isLoading: boolean;
  /** Whether the current user has an owner role. */
  isOwner: boolean;
  /** Whether the current user has an admin role. */
  isAdmin: boolean;
  /** Whether the current user has at least manager-level access (owner, admin, or manager). */
  isManagerOrAbove: boolean;
  /** Check if the current role has a specific permission. */
  hasPermission: (permission: Permission) => boolean;
}

/**
 * useWorkspaceRole
 *
 * Provides the current user's role in the active workspace along with
 * convenience boolean flags for common permission checks.
 *
 * @example
 * ```ts
 * const { currentMemberRole, isOwner, isAdmin, isManagerOrAbove, hasPermission } = useWorkspaceRole();
 *
 * if (!isManagerOrAbove) {
 *   return <p>You do not have permission to perform this action.</p>;
 * }
 *
 * if (hasPermission('manage_team')) {
 *   // Show team management UI
 * }
 * ```
 */
export function useWorkspaceRole(): WorkspaceRoleState {
  const currentMemberRole = useWorkspaceStore((state) => state.currentMemberRole);
  const setCurrentMemberRole = useWorkspaceStore((state) => state.setCurrentMemberRole);
  const isWorkspaceLoading = useWorkspaceStore((state) => state.isLoading);

  const isOwner = currentMemberRole === 'owner';
  const isAdmin = currentMemberRole === 'admin';
  const isManagerOrAbove =
    currentMemberRole === 'owner' ||
    currentMemberRole === 'admin' ||
    currentMemberRole === 'manager';

  const hasRole = currentMemberRole !== null;

  const hasPermission = (permission: Permission): boolean => {
    if (!currentMemberRole) return false;
    return roleHasPermission(currentMemberRole, permission);
  };

  return {
    currentMemberRole,
    role: currentMemberRole,
    setCurrentMemberRole,
    hasRole,
    isLoading: isWorkspaceLoading,
    isOwner,
    isAdmin,
    isManagerOrAbove,
    hasPermission,
  };
}